// Welcome-bumper template tokens.
//
// Lets the owner write personalised greetings like
//   "Welcome back, {name}. {newtapes} since you last tuned in."
// without per-viewer server logic. Interpolation runs entirely client-side
// (everything we need — name, new-tape count, current channel, date —
// already lives in CRT/App state at render time), which keeps the backend
// dumb and avoids leaking viewer identity through any per-request endpoint.
//
// Anything inside `{ ... }` that isn't a known token is left untouched so
// stray curly braces in the owner's copy don't disappear silently.

export const WELCOME_TOKENS = [
  { token: "{name}",     label: "Viewer's first name (e.g. \"Lex\")" },
  { token: "{fullname}", label: "Viewer's full name (e.g. \"Lex Carter\")" },
  { token: "{greeting}", label: "Time-of-day greeting (\"Good morning\" / \"Good afternoon\" / \"Good evening\")" },
  { token: "{new}",      label: "Number of new tapes since their last visit (e.g. 3)" },
  { token: "{newtapes}", label: "Pluralised \"N NEW TAPE(S)\" or empty if none" },
  { token: "{ch}",       label: "Current channel name (e.g. \"NIGHTCRAWLER MOVIE\")" },
  { token: "{chnum}",    label: "Current channel number, zero-padded (e.g. \"03\")" },
  { token: "{date}",     label: "Today's date in the viewer's locale" },
  { token: "{time}",     label: "Current time in the viewer's locale (HH:MM)" },
  { token: "{callsign}", label: "Station callsign (e.g. \"CRT-1986\")" },
];

function firstName(full) {
  if (!full) return "";
  const trimmed = String(full).trim();
  if (!trimmed) return "";
  const space = trimmed.indexOf(" ");
  return space === -1 ? trimmed : trimmed.slice(0, space);
}

function formatNewTapes(count) {
  const n = Number(count) || 0;
  if (n <= 0) return "";
  return `${n} NEW TAPE${n === 1 ? "" : "S"}`;
}

function timeOfDayGreeting(date) {
  // Bracket the viewer's LOCAL clock into three broadcast-station-style
  // greetings. Late-night (after 22:00 / before 05:00) folds into
  // "Good evening" so a 1 AM tune-in still feels like prime-time TV
  // rather than getting a weirdly-cheerful "Good morning".
  const h = date.getHours();
  if (h >= 5 && h < 12) return "Good morning";
  if (h >= 12 && h < 17) return "Good afternoon";
  return "Good evening";
}

export function interpolateWelcome(template, ctx) {
  if (!template || typeof template !== "string") return "";
  const safe = (v) => (v === undefined || v === null ? "" : String(v));
  const now = ctx && ctx.now instanceof Date ? ctx.now : new Date();
  const replacements = {
    "{name}":     safe(firstName(ctx && ctx.viewerName)),
    "{fullname}": safe(ctx && ctx.viewerName),
    "{greeting}": timeOfDayGreeting(now),
    "{new}":      safe(ctx && ctx.newCount ? ctx.newCount : ""),
    "{newtapes}": formatNewTapes(ctx && ctx.newCount),
    "{ch}":       safe(ctx && ctx.channelName),
    "{chnum}":    safe(ctx && ctx.channelNumberLabel),
    "{date}":     safe(now.toLocaleDateString()),
    "{time}":     safe(now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })),
    "{callsign}": safe(ctx && ctx.callsign),
  };
  let out = template;
  for (const [token, value] of Object.entries(replacements)) {
    if (out.includes(token)) {
      out = out.split(token).join(value);
    }
  }
  return out;
}
